import type { ColDef, ITooltipParams } from 'ag-grid-community';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { get } from 'lodash-es';
import { memo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TextColumnParams } from '../BlotterTable/columns/text';
import { EllipsisBoxWithTooltip } from '../Form/BaseSelect/EllipsisBoxWithTooltip';
import { Text } from '../Text';

export type AgGridTextParams = {
  columnParams: TextColumnParams;
  /** When true, render `columnParams.placeholder` when the formatted display is empty (e.g. `textLiveEdit`). */
  showPlaceholderWhenEmpty?: boolean;
};

type AgGridTextProps = ICellRendererParams & AgGridTextParams;

function isDisplayEmpty(formatted: string | undefined | null): boolean {
  if (formatted == null || formatted === '') {
    return true;
  }
  return formatted.trim() === '';
}

function readAgGridCellTooltipCopy(props: AgGridTextProps): string | undefined {
  const colDef = props.colDef as ColDef | undefined;
  if (colDef == null) {
    return undefined;
  }

  let value: string | null | undefined;
  if (colDef.tooltipField && props.data != null && typeof props.data === 'object') {
    const fieldValue = get(props.data, colDef.tooltipField);
    value = typeof fieldValue === 'string' ? fieldValue : undefined;
  } else if (typeof colDef.tooltipValueGetter === 'function') {
    // Cell renderers do not include `location`; AG Grid's getter type requires ITooltipParams.
    const tooltipParams = {
      ...props,
      location: 'cell',
      rowIndex: props.node?.rowIndex,
    } as ITooltipParams;
    const tooltipValue = colDef.tooltipValueGetter(tooltipParams);
    value = typeof tooltipValue === 'string' ? tooltipValue : undefined;
  }

  return value != null && value !== '' ? value : undefined;
}

/**
 * Suppress the ellipsis tooltip when:
 * - Explicit `suppressOverflowTooltip` is true
 * - This row's AG Grid cell tooltip would show copy: `tooltipField` when row data exists, otherwise `tooltipValueGetter`.
 */
function resolveSuppressOverflowTooltip(props: AgGridTextProps): boolean {
  const explicit = props.columnParams.suppressOverflowTooltip;
  if (explicit != null) {
    return explicit;
  }
  return readAgGridCellTooltipCopy(props) != null;
}

function AgGridTextInner(props: ICellRendererParams & AgGridTextParams): ReactNode {
  if (!props.column || !props.node) {
    return null;
  }

  const { columnParams, showPlaceholderWhenEmpty } = props;
  const formatted = props.valueFormatted ?? '';
  const { placeholder, suppressEllipsize } = columnParams;
  const suppressOverflowTooltip = resolveSuppressOverflowTooltip(props);

  if (showPlaceholderWhenEmpty && placeholder != null && isDisplayEmpty(formatted)) {
    const placeholderColor = 'colorTextMuted';
    return suppressEllipsize ? (
      <Text color={placeholderColor}>{placeholder}</Text>
    ) : (
      <EllipsisBoxWithTooltip
        tooltipProps={{ placement: 'bottom-start' }}
        color={placeholderColor}
        suppressOverflowTooltip={suppressOverflowTooltip}
      >
        <Text color={placeholderColor}>{placeholder}</Text>
      </EllipsisBoxWithTooltip>
    );
  }

  const textColor =
    props.column.isCellEditable(props.node) && placeholder != null && !props.valueFormatted
      ? (columnParams.color ?? 'colorTextSubtle')
      : columnParams.color;

  const href = columnParams.getHref?.(props as ICellRendererParams & TextColumnParams);
  const content = formatted;
  const linkStyle = { color: textColor, textDecoration: 'underline' };

  const renderContent = (children: ReactNode) => {
    if (href) {
      return (
        <Link to={href} style={linkStyle}>
          {children}
        </Link>
      );
    }
    return <Text color={textColor}>{children}</Text>;
  };

  return suppressEllipsize ? (
    renderContent(content)
  ) : (
    <EllipsisBoxWithTooltip
      tooltipProps={{ placement: 'bottom-start' }}
      color={textColor}
      suppressOverflowTooltip={suppressOverflowTooltip}
    >
      {renderContent(content)}
    </EllipsisBoxWithTooltip>
  );
}

export const AgGridText = memo(AgGridTextInner);
